import React from 'react';
import { jsPDF } from 'jspdf';
import '../../assets/fonts/arial-normal';
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  AlignmentType,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
  Footer,
} from 'docx';
import { saveAs } from 'file-saver';

export interface OfferLetterProps {
  details: {
    employeeName: string;
    email: string;
    designation: string;
    department: string;
    role: string;
    employmentType: string;
    dateOfJoining: string;
    companyName: string;
    companyAddress: string;
    manager: string;
    hr: string;
    referenceNo: string;
  };
  isDoc?: boolean;
}

function AiOfferLetter({ details, isDoc = false }: OfferLetterProps) {
  const {
    employeeName,
    email,
    designation,
    department,
    role,
    employmentType,
    dateOfJoining,
    companyName,
    companyAddress,
    manager,
    hr,
    referenceNo,
  } = details;

  const formattedDate = new Date(dateOfJoining).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  const isFullTime =
    employmentType?.toLowerCase().replace(/[\s-]/g, '') === 'fulltime';
  const roleText = isFullTime
    ? `We are pleased to offer you employment effective ${formattedDate}. You will be appointed as ${designation} in the ${department} department.`
    : `We are pleased to offer you the position of ${designation}, effective ${formattedDate}, in the ${department} department for the role of ${role}.`;

  const generatePDF = () => {
    const doc = new jsPDF();
    const marginLeft = 20;
    const lineSpacing = 6;
    let cursorY = 30;

    doc.setFont('arial', 'bold');
    doc.setTextColor(119, 104, 187);
    doc.setFontSize(13);
    doc.text(`${employmentType.toUpperCase()} OFFER LETTER`, marginLeft, cursorY);
    cursorY += 10;

    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.setFont('arial', 'bold');
    doc.text('PRIVATE & CONFIDENTIAL', marginLeft, cursorY);

    doc.setFont('arial', 'normal');
    doc.setFontSize(10);
    cursorY += lineSpacing;
    doc.text(`To:`, marginLeft, cursorY);
    cursorY += lineSpacing;
    doc.text(employeeName, marginLeft, cursorY);
    cursorY += lineSpacing;
    doc.text(`Email: ${email}`, marginLeft, cursorY);
    cursorY += lineSpacing;
    doc.text(`REF: ${referenceNo}`, marginLeft, cursorY);
    cursorY += lineSpacing;
    doc.text(`Date: ${formattedDate}`, marginLeft, cursorY);
    cursorY += lineSpacing * 2;
    doc.text(`Dear ${employeeName},`, marginLeft, cursorY);
    cursorY += lineSpacing * 1.5;

    const body = `${roleText}

This engagement is offered on the understanding that all information provided by you in your application or interview process is true and accurate. If it is found at any point that the details are incorrect or incomplete, this engagement may be terminated without prior notice.

You are expected to fulfill your responsibilities as agreed, maintain professional conduct, and follow any instructions issued by the organization or your reporting supervisor. Work timings and mode (on-site/remote) will be as per project requirements and communicated to you in advance.

Please keep all terms of this engagement, including your compensation and allowances, confidential. Sharing this information with anyone other than authorized personnel may lead to disciplinary action and termination of this engagement.

Other standard terms regarding confidentiality, non-disclosure, intellectual property, and acceptable conduct shall apply throughout your association with the organization and beyond, as required. Please sign and return a copy of this letter to confirm your acceptance.`;

    const bodyLines = doc.splitTextToSize(body, 170);
    doc.text(bodyLines, marginLeft, cursorY);
    cursorY += bodyLines.length * 4.5 + lineSpacing;

    doc.setFont('arial', 'bold');
    doc.text(`For ${companyName}`, marginLeft, cursorY);
    cursorY += lineSpacing;
    doc.setFont('arial', 'normal');
    doc.text(manager, marginLeft, cursorY);
    cursorY += lineSpacing;
    doc.text(hr, marginLeft, cursorY);
    cursorY += lineSpacing;
    doc.text('Date: _______________', marginLeft, cursorY);

    cursorY += lineSpacing * 2;
    doc.setFont('arial', 'bold');
    doc.text('Accepted and Agreed by:', marginLeft, cursorY);
    cursorY += lineSpacing;
    doc.setFont('arial', 'normal');
    doc.text(employeeName, marginLeft, cursorY);
    cursorY += lineSpacing;
    doc.text('Date: _______________', marginLeft, cursorY);

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const baseHeight = 12;
    const topHeight = 8;

    doc.setFillColor('#8073C0');
    doc.rect(0, pageHeight - baseHeight, pageWidth, baseHeight, 'F');
    doc.setFillColor('#323595');
    doc.rect(
      0,
      pageHeight - baseHeight - topHeight + 4,
      pageWidth,
      topHeight,
      'F'
    );

    const filename = `${employeeName.replace(/\s+/g, '_')}_${employmentType
      .toLowerCase()
      .replace(/\s+/g, '_')}_offer_letter.pdf`;

    doc.save(filename);
  };

  const downloadDocx = async () => {
    const doc = new Document({
      sections: [
        {
          properties: {
            page: {
              margin: {
                top: 1940,
                bottom: 720,
                left: 0,
                right: 0,
                footer: 0,
              },
            },
          },

          footers: {
            default: new Footer({
              children: [
                new Paragraph({
                  children: [
                    new Table({
                      width: { size: 100, type: WidthType.PERCENTAGE },
                      rows: [
                        new TableRow({
                          children: [
                            new TableCell({
                              margins: { left: 0, right: 0, top: 0, bottom: 0 },
                              shading: { fill: '323595' },
                              borders: {
                                top: { style: BorderStyle.NONE },
                                bottom: { style: BorderStyle.NONE },
                                left: { style: BorderStyle.NONE },
                                right: { style: BorderStyle.NONE },
                              },
                              children: [],
                            }),
                          ],
                        }),
                        new TableRow({
                          children: [
                            new TableCell({
                              margins: { left: 0, right: 0, top: 0, bottom: 0 },
                              shading: { fill: '8073C0' },
                              borders: {
                                top: { style: BorderStyle.NONE },
                                bottom: { style: BorderStyle.NONE },
                                left: { style: BorderStyle.NONE },
                                right: { style: BorderStyle.NONE },
                              },
                              children: [],
                            }),
                          ],
                        }),
                      ],
                    }),
                  ],
                  alignment: AlignmentType.LEFT,
                  spacing: { before: 0, after: 0 },
                }),
              ],
            }),
          },

          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: `${employmentType.toUpperCase()} OFFER LETTER`,
                  bold: true,
                  font: 'Arial',
                  size: 32,
                  color: '775CBB',
                }),
              ],
              spacing: { after: 120 },
              alignment: AlignmentType.LEFT,
              indent: { left: 720, right: 720 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: 'PRIVATE & CONFIDENTIAL',
                  bold: true,
                  font: 'Arial',
                  size: 32,
                }),
              ],
              spacing: { after: 120 },
              indent: { left: 720, right: 720 },
            }),

            new Paragraph({
              children: [new TextRun({ text: `To:`, font: 'Arial', size: 26 })],
              indent: { left: 720, right: 720 },
            }),
            new Paragraph({
              children: [
                new TextRun({ text: employeeName, font: 'Arial', size: 26 }),
              ],
              indent: { left: 720, right: 720 },
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `Email: ${email}`, font: 'Arial', size: 26 }),
              ],
              indent: { left: 720, right: 720 },
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: `REF: ${referenceNo}`,
                  font: 'Arial',
                  size: 26,
                }),
              ],
              indent: { left: 720, right: 720 },
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: `Date: ${formattedDate}`,
                  font: 'Arial',
                  size: 26,
                }),
              ],
              indent: { left: 720, right: 720 },
            }),
            new Paragraph({ children: [new TextRun({ text: '' })] }),
            new Paragraph({
              children: [
                new TextRun({
                  text: `Dear ${employeeName},`,
                  font: 'Arial',
                  size: 26,
                  bold: true,
                }),
              ],
              indent: { left: 720, right: 720 },
            }),
            new Paragraph({ children: [new TextRun({ text: '' })] }),

            new Paragraph({
              children: [
                new TextRun({
                  text: roleText,
                  font: 'Arial',
                  size: 24,
                }),
              ],
              indent: { left: 720, right: 720 },

              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 200, line: 276 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: 'This engagement is offered on the understanding that all information provided by you in your application or interview process is true and accurate. If it is found at any point that the details are incorrect or incomplete, this engagement may be terminated without prior notice.',
                  font: 'Arial',
                  size: 24,
                }),
              ],
              indent: { left: 720, right: 720 },

              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 200, line: 276 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: 'You are expected to fulfill your responsibilities as agreed, maintain professional conduct, and follow any instructions issued by the organization or your reporting supervisor. Work timings and mode (on-site/remote) will be as per project requirements and communicated to you in advance.',
                  font: 'Arial',
                  size: 24,
                }),
              ],
              indent: { left: 720, right: 720 },
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 200, line: 276 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: 'Please keep all terms of this engagement, including your compensation and allowances, confidential. Sharing this information with anyone other than authorized personnel may lead to disciplinary action and termination of this engagement.',
                  font: 'Arial',
                  size: 24,
                }),
              ],
              indent: { left: 720, right: 720 },

              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 200, line: 276 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: 'Other standard terms regarding confidentiality, non-disclosure, intellectual property, and acceptable conduct shall apply throughout your association with the organization and beyond, as required. Please sign and return a copy of this letter to confirm your acceptance.',
                  font: 'Arial',
                  size: 24,
                }),
              ],
              indent: { left: 720, right: 720 },
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 200, line: 276 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: `For ${companyName}`,
                  bold: true,
                  font: 'Arial',
                  size: 24,
                }),
              ],
              indent: { left: 720, right: 720 },
            }),
            new Paragraph({
              children: [
                new TextRun({ text: manager, font: 'Arial', size: 20 }),
              ],
              indent: { left: 720, right: 720 },
            }),
            new Paragraph({
              children: [
                new TextRun({ text: hr, font: 'Arial', size: 22 }),
              ],
              indent: { left: 720, right: 720 },
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: 'Date: _______________',
                  font: 'Arial',
                  size: 22,
                }),
              ],
              indent: { left: 720, right: 720 },
            }),
            new Paragraph({ children: [new TextRun({ text: '' })] }),

            new Paragraph({
              children: [
                new TextRun({
                  text: 'Accepted and Agreed by:',
                  bold: true,
                  font: 'Arial',
                  size: 22,
                }),
              ],
              indent: { left: 720, right: 720 },
            }),
            new Paragraph({
              children: [
                new TextRun({ text: employeeName, font: 'Arial', size: 20 }),
              ],
              indent: { left: 720, right: 720 },
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: 'Date: _______________',
                  font: 'Arial',
                  size: 22,
                }),
              ],
              indent: { left: 720, right: 720 },
            }),
            new Paragraph({ children: [new TextRun({ text: '' })] }),
          ],
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    const filename = `${employeeName.replace(/\s+/g, '_')}_${employmentType
      .toLowerCase()
      .replace(/\s+/g, '_')}_offer_letter.docx`;
    saveAs(blob, filename);
  };

  return (
    <div>
      <button
        onClick={isDoc ? downloadDocx : generatePDF}
        style={{
          backgroundColor: '#8254ff',
          color: 'white',
          padding: '10px 16px',
          fontSize: '14px',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer',
        }}
      >
        Download Offer Letter
      </button>
    </div>
  );
}

export default AiOfferLetter;
